import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGrid,
  faList,
  faPlus,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, LoadingModule, PageHeader, Tools,
} from '@domain-group/suit-theme'

import { useNumericParams, useSetState } from '@domain-group/react-hooks'

import useCampaignComparable from '@hooks/useCampaignComparable'
import useCampaignComparables from '@hooks/useCampaignComparables'
import useCampaignSelector from '@hooks/useCampaignSelector'
import useCurrentUser from '@hooks/useCurrentUser'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import propertyDetailsTabBarItems from '@sections/Client/packs/Campaign/utils/generatePropertyDetailsTabBarItems'

import { getCampaignPermissions, getCampaignType } from 'shared/functions/campaign'
import CampaignComparableBlankState from '../CampaignComparableBlankState'
import Comparables from '../Comparables'

const defaultState = {
  gridView: false,
}

const PageContent = () => {
  const [state, setState] = useSetState(defaultState)
  const { gridView } = state

  const { campaignId: campaign_id } = useNumericParams()

  const agencyComparablesPayload = useCampaignComparables({
    filters: {
      campaign_id,
      comparable_source: 'agency',
    },
    performHttpRequests: !!campaign_id,
    requestOptions: {
      include_campaign_comparable_image: true,
      // @ts-expect-error TS2353
      include_image_sizes: true,
    },
  })

  const marketComparablesPayload = useCampaignComparables({
    filters: {
      campaign_id,
      comparable_source: 'market',
    },
    performHttpRequests: !!campaign_id,
    requestOptions: {
      include_campaign_comparable_image: true,
      // @ts-expect-error TS2353
      include_image_sizes: true,
    },
  })

  const {
    hasFilteredCampaignComparables: hasFilteredAgencyCampaignComparables,
    loading: loadingAgencyComparables,
  } = agencyComparablesPayload

  const {
    hasFilteredCampaignComparables: hasFilteredMarketCampaignComparables,
    loading: loadingMarketComparables,
  } = marketComparablesPayload

  const hasFilteredCampaignComparables = hasFilteredAgencyCampaignComparables || hasFilteredMarketCampaignComparables
  const loading = loadingAgencyComparables && loadingMarketComparables

  const { callbacks: { addCampaignComparable } } = useCampaignComparable({ campaign_id })

  const { currentUser } = useCurrentUser()

  const { campaign, campaign: { address } } = useCampaignSelector()
  const permissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Comparables</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addCampaignComparable()}
            size="medium"
          >
            Create Comparable
          </Button>
        )}
        activeTabBarItemKey="comparables"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={propertyDetailsTabBarItems(campaign_id, permissions)}
        title="Comparables"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.comparables.banner" />

            <LoadingModule loading={loading} times={3} />

            {!hasFilteredCampaignComparables && !loading && <CampaignComparableBlankState />}

            {!loading && hasFilteredMarketCampaignComparables && (
              <Comparables
                comparablesPayload={marketComparablesPayload}
                gridView={gridView}
                title="Market Comparables"
              />
            )}

            {!loading && hasFilteredAgencyCampaignComparables && (
              <Comparables
                comparablesPayload={agencyComparablesPayload}
                gridView={gridView}
                title="Agency Comparables"
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.comparables.sidebar" />

            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={gridView ? faList : faGrid} />}
                onClick={() => setState({ gridView: !gridView })}
                size="medium"
              >
                {gridView ? 'List View ' : 'Grid View ' }
              </Button>
            </Tools>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
